import { EnterKeyBehaviorOptions } from '@web/components/EnterKeyBehaviorOptions'
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

/** Keep sending and changing the account's keyboard behavior on one accessible trigger. */
export function SendButton({
  onSend,
  disabled = false,
  label,
  className,
  children,
}: {
  onSend: () => void
  disabled?: boolean
  label: string
  className: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<CSSProperties>({})
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pointer = useRef<{ x: number; y: number } | null>(null)
  const suppressClick = useRef(false)
  const id = useId()
  const clearPress = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    pointer.current = null
  }
  const close = (focus = true) => {
    setOpen(false)
    if (focus) trigger.current?.focus()
  }
  const show = () => {
    const bounds = trigger.current?.getBoundingClientRect()
    if (!bounds) return
    const viewport = window.visualViewport
    const top = viewport?.offsetTop ?? 0
    const left = viewport?.offsetLeft ?? 0
    const width = viewport?.width ?? window.innerWidth
    const height = viewport?.height ?? window.innerHeight
    const menuWidth = Math.min(340, width - 16)
    const above = bounds.top - top - 16
    const below = top + height - bounds.bottom - 16
    const down = below > above
    setPosition({
      left: Math.max(
        left + 8,
        Math.min(bounds.right - menuWidth, left + width - menuWidth - 8),
      ),
      ...(down
        ? { top: bounds.bottom + 8 }
        : { bottom: window.innerHeight - bounds.top + 8 }),
      width: menuWidth,
      maxHeight: Math.max(80, down ? below : above),
    })
    setOpen(true)
  }
  useEffect(() => () => clearPress(), [])
  useEffect(() => {
    if (!open) return
    ;(
      menu.current?.querySelector<HTMLElement>('[aria-checked="true"]') ??
      menu.current
    )?.focus()
    const outside = (event: PointerEvent) => {
      if (
        !menu.current?.contains(event.target as Node) &&
        !trigger.current?.contains(event.target as Node)
      )
        close(false)
    }
    const resize = () => close(false)
    document.addEventListener('pointerdown', outside)
    window.addEventListener('resize', resize)
    window.visualViewport?.addEventListener('resize', resize)
    return () => {
      document.removeEventListener('pointerdown', outside)
      window.removeEventListener('resize', resize)
      window.visualViewport?.removeEventListener('resize', resize)
    }
  }, [open])
  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`${className} ${disabled ? 'opacity-50' : ''}`}
        aria-disabled={disabled}
        aria-label={label}
        aria-haspopup="menu"
        aria-controls={open ? id : undefined}
        aria-expanded={open}
        title={`${label}. Long-press or right-click for Enter Key Behavior.`}
        style={{
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          userSelect: 'none',
        }}
        onClick={() => {
          if (suppressClick.current) {
            suppressClick.current = false
            return
          }
          if (!disabled) {
            setOpen(false)
            onSend()
          }
        }}
        onContextMenu={(event) => {
          event.preventDefault()
          suppressClick.current = true
          clearPress()
          show()
        }}
        onPointerDown={(event) => {
          if (event.button !== 0) return
          suppressClick.current = false
          clearPress()
          pointer.current = { x: event.clientX, y: event.clientY }
          timer.current = setTimeout(() => {
            suppressClick.current = true
            show()
          }, 500)
        }}
        onPointerMove={(event) => {
          if (
            pointer.current &&
            Math.hypot(
              event.clientX - pointer.current.x,
              event.clientY - pointer.current.y,
            ) > 8
          )
            clearPress()
        }}
        onPointerUp={clearPress}
        onPointerCancel={clearPress}
        onPointerLeave={clearPress}
        onKeyDown={(event) => {
          if (
            event.key === 'ContextMenu' ||
            (event.shiftKey && event.key === 'F10') ||
            event.key === 'ArrowUp' ||
            event.key === 'ArrowDown'
          ) {
            event.preventDefault()
            show()
          } else if (event.key === 'Enter' || event.key === ' ')
            suppressClick.current = false
        }}
      >
        {children}
      </button>
      {open &&
        createPortal(
          <div
            ref={menu}
            id={id}
            role="menu"
            tabIndex={-1}
            aria-label="Enter Key Behavior"
            className="pill-menu fixed overflow-y-auto rounded-card border border-line p-1 shadow-card"
            style={position}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault()
                event.stopPropagation()
                close()
                return
              }
              if (event.key === 'Tab') {
                close()
                return
              }
              if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key))
                return
              event.preventDefault()
              const choices = [
                ...event.currentTarget.querySelectorAll<HTMLButtonElement>(
                  '[role="menuitemradio"]:not(:disabled)',
                ),
              ]
              const current = choices.indexOf(
                document.activeElement as HTMLButtonElement,
              )
              choices[
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? choices.length - 1
                    : (current +
                        (event.key === 'ArrowDown' ? 1 : -1) +
                        choices.length) %
                      choices.length
              ]?.focus()
            }}
          >
            <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted">
              Enter Key Behavior
            </div>
            <EnterKeyBehaviorOptions menu onSelected={() => close()} />
          </div>,
          trigger.current?.closest('[role="dialog"]') ?? document.body,
        )}
    </>
  )
}
